import React, {
  useEffect, useState, useRef, useId,
} from 'react';
import { PropTypes } from 'prop-types';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { isEqual } from 'lodash';
import { DialogsProvider, ToastProvider } from '../../../providers';
import { withContextProvider } from '../../../hoc';
import Table from '../../../componentsV2/table/Table';
import { useDialogs, useToasts, useUnsavedChangesWarning } from '../../../hooks';
import Button from '../../../componentsV2/Button/Button';
import SeasonsActions from '../../../componentsV2/Seasons/SeasonsActions';
import {
  createSeason, getSeasons, removeSeason, updateSeason,
} from '../../../utils/api/seasons';
import { getSeasonExport, getFile } from '../../../utils/api/exports';
import NewSeasonModal from '../../../componentsV2/Seasons/NewSeasonModal';
import ExportSeasonsModal from '../../../componentsV2/Seasons/ExportSeasonsModal';
import SummaryComponent from '../../../componentsV2/Seasons/SummaryComponent';
import EditSeason from '../../../componentsV2/Seasons/EditSeason';
import EmptyState from '../../../componentsV2/EmptyState/EmptyState';
import { Plus } from '../../../componentsV2/Icons/Icons';
import Pagination from '../../../componentsV2/pagination/Pagination';
import Breadcrumbs from '../../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../../componentsV2/Headers/PagesHeader';
import tailwindTheme from '../../../../../../config/tailwind.config';
import { updateSearchParamsAndUrl } from '../../../utils/updateSearchParamsAndUrl';
import { formatNumericDate, formatImportDate } from '../../../utils/dates';
import { getRoleChecks } from '../../../utils/roles';
import { useUrlModals } from '../../../utils/useUrlModals';
import { closeDialogWithUnsavedChanges } from '../../../utils/dialogs';

const addAction = (arr, isManager, isPlanner, showDeleteModal, handleExport, handleSeasonEdit, seasonsDataRef) => {
  const SeasonsWithActions = arr.map((season) => {
    const { id } = season;
    return {
      ...season,
      action: (
        <SeasonsActions
          isManager={isManager}
          isPlanner={isPlanner}
          onDelete={() => showDeleteModal(id)}
          onExport={() => handleExport(id)}
          onEdit={() => {
            const seasonToEdit = seasonsDataRef.current.find((s) => s.id === season.id);
            handleSeasonEdit({ ...seasonToEdit });
          }}
        />
      ),
    };
  });
  return SeasonsWithActions;
};

// Helper function to update seasons data and total count
const updateSeasonData = (
  data,
  setSeasonsData,
  setTotalSeasons,
  isManager,
  isPlanner,
  showDeleteModal,
  handleExport,
  handleSeasonEdit,
  seasonsDataRef,
) => {
  setSeasonsData(
    addAction(data.seasons, isManager, isPlanner, showDeleteModal, handleExport, handleSeasonEdit, seasonsDataRef),
  );
  setTotalSeasons(data.total_count);
};

const SeasonsPage = ({ organization, currentUserRole }) => {
  const { isManager, isPlanner } = getRoleChecks(currentUserRole);
  const orgId = organization.id;
  const { showToast, hideToast } = useToasts();
  const itemsPerPage = 20;
  const [params, setParams] = useState({
    sortBy: '',
    direction: '',
    limit: itemsPerPage,
    offset: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSeasons, setTotalSeasons] = useState(0); // Total seasons for pagination

  // URL-driven modal state
  const { modals, openModal, closeModal } = useUrlModals({
    addSeason: { isIdParam: false },
    exportSeasonId: { isIdParam: true },
    deleteSeasonId: { isIdParam: true },
  });

  // Export modal status that isn’t part of the URL
  const [exportStatus, setExportStatus] = useState({ completed: false, failed: false });

  // variables to follow DRY principle. Do not mutate initialSeason and initialDataForValidationErrors variable!
  const initialSeason = {
    id: null,
    name: '',
    start_date: '',
    end_date: '',
  };
  const initialDataForValidationErrors = { name: '', start_date: '', end_date: '' };
  const [seasonOnEdit, setSeasonOnEdit] = useState(initialSeason);
  const seasonOnEditRef = useRef(initialSeason);
  const [updatedSeasonId, setUpdatedSeasonId] = useState(null);
  const seasonsDataRef = useRef([]);
  const [seasonsData, setSeasonsData] = useState([]);
  const toastId = useId(); // generate a unique id for the toast that informs the user that the export is being prepared. It is only for export functionality
  const { openDialog, closeDialog } = useDialogs();
  const [validationErrors, setValidationErrors] = useState(initialDataForValidationErrors);
  const [seasonUpdateErrors, setSeasonUpdateErrors] = useState(initialDataForValidationErrors);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const hasUnsavedChangesRef = useRef(false);

  const originalSeasonOnEdit = seasonsData.find((s) => s.id === seasonOnEdit.id);
  const normalizeDateString = (value) => value?.split('T')[0] || ''; // normalizes changes within the ISO date times so that unsavedChanges doesn't get stuck on true
  const hasUnsavedChanges = seasonOnEdit.id !== null && !isEqual({
    name: seasonOnEdit.name,
    start_date: normalizeDateString(seasonOnEdit.start_date),
    end_date: normalizeDateString(seasonOnEdit.end_date),
  }, {
    name: originalSeasonOnEdit?.name,
    start_date: normalizeDateString(originalSeasonOnEdit?.start_date),
    end_date: normalizeDateString(originalSeasonOnEdit?.end_date),
  });

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges, seasonsData]);

  useEffect(() => {
    seasonOnEditRef.current = seasonOnEdit;
  }, [seasonOnEdit]);

  // browser warning
  useUnsavedChangesWarning(isUnsavedChanges);

  // Reads refs (not state) because this function is captured in row action
  // closures baked once at fetch time by addAction, so state would be stale.
  const handleSeasonEdit = (seasonToEdit) => {
    if (hasUnsavedChangesRef.current && seasonOnEditRef.current.id !== seasonToEdit.id) {
      closeDialogWithUnsavedChanges(
        openDialog,
        closeDialog,
        () => setSeasonOnEdit({ ...seasonToEdit }),
        setIsUnsavedChanges,
      );
      return;
    }
    setSeasonOnEdit({ ...seasonToEdit });
  };

  useEffect(() => {
    seasonsDataRef.current = seasonsData;
  }, [seasonsData]);

  const handleExport = async (id) => {
    const exportToastDuration = 0; // set to 0 to keep the toast open until the export is ready
    const errorMessage = 'There was an error exporting season. Please try again or contact us.';
    const season = seasonsDataRef.current.find(seasons => seasons.id === id);

    if (!season) {
      showToast({ message: `Season not found for ID ${id}`, type: 'warning' });
      return;
    }

    setExportStatus({ completed: false, failed: false });
    openModal('exportSeasonId', id);

    try {
      const response = await getSeasonExport(orgId, id);

      if (response.ok) {
        showToast({ message: 'Preparing export', type: 'info', isSpinner: true }, exportToastDuration, toastId); // this toast needs to be manually hidden
        const data = await response.json();
        const exportId = data.export_file.id;
        const { filename } = data.export_file;
        await getFile({
          exportId, organizationId: orgId, filename, showToast, hideToast, toastId, type: 'seasons', errorMessage,
        });
        setExportStatus({ completed: true, failed: false });
      } else {
        setExportStatus({ completed: true, failed: true });
        throw new Error('Export initiation failed');
      }
    } catch (error) {
      showToast({ message: error.message, type: 'error' });
    }
  };

  const handleDelete = (id) => {
    removeSeason(id, orgId)
      .then(resp => {
        if (resp.status === 200) {
          closeModal('deleteSeasonId');
          showToast({ message: 'Season has been deleted', type: 'success' });
          getSeasons(orgId, params)
            .then(response => response.json())
            // eslint-disable-next-line no-use-before-define
            .then(data => updateSeasonData(data, setSeasonsData, setTotalSeasons, isManager, isPlanner, showDeleteModal, handleExport, handleSeasonEdit, seasonsDataRef))
            .catch(error => showToast({ message: error.message, type: 'error' }));
        }
      })
      .catch(error => showToast({ message: error.message, type: 'error' }));
  };

  const showDeleteModal = (id) => {
    const season = seasonsDataRef.current.find((s) => s.id === id);
    if (season) {
      openDialog({
        message: [
          <p className="text-center leading-snug">
            Delete season <b>{season.name}</b>?
          </p>,
        ],
        show: true,
        actions: [
          {
            label: 'Cancel',
            variant: 'secondary',
            onClick: closeDialog,
          },
          {
            label: 'Delete season',
            variant: 'primary',
            onClick: () => {
              handleDelete(id);
              closeDialog();
            },
          },
        ],
      });
    } else {
      showToast({ message: `Season not found for ID ${id}`, type: 'warning' });
    }
  };

  const onUpdate = () => {
    const errorsObject = { ...initialDataForValidationErrors };
    const seasonExists = seasonsData.some((season) => season.id !== seasonOnEdit.id && season.name.toLowerCase() === seasonOnEdit.name.toLowerCase());
    if (seasonExists) {
      errorsObject.name = 'Season name already exists.';
    }
    if (seasonOnEdit.name === '') {
      errorsObject.name = 'Season name can’t be blank.';
    }
    if (!seasonOnEdit.start_date) {
      errorsObject.start_date = 'Start date is required.';
    }
    if (!seasonOnEdit.end_date) {
      errorsObject.end_date = 'End date is required.';
    }
    if (new Date(seasonOnEdit.start_date).getTime() > new Date(seasonOnEdit.end_date).getTime()) {
      errorsObject.end_date = `End date must be after ${formatImportDate(seasonOnEdit.start_date)}.`;
    }
    if (Object.values(errorsObject).some((value) => value !== '')) {
      setSeasonUpdateErrors(errorsObject);
      Object.keys(errorsObject).forEach(key => { if (errorsObject[key] !== '') showToast({ message: errorsObject[key], type: 'error' }); });
      return;
    }
    const payload = {
      ...seasonOnEdit,
      start_date: new Date(seasonOnEdit.start_date).toISOString(),
      end_date: new Date(seasonOnEdit.end_date).toISOString(),
    };
    updateSeason(payload, orgId, seasonOnEdit.id)
      .then((resp) => {
        if (resp.status === 200 || resp.status === 422) return resp.json();
        return () => {};
      })
      .then((data) => {
        if (data.season) {
          const [updatedSeason] = addAction(
            [data.season], isManager, isPlanner, showDeleteModal, handleExport, handleSeasonEdit, seasonsDataRef,
          );
          setSeasonsData(prev => prev.map(s => (s.id === data.season.id ? updatedSeason : s)));
          setSeasonOnEdit(initialSeason);
          setUpdatedSeasonId(data.season.id);
          setTimeout(() => {
            setUpdatedSeasonId(null);
          }, 2000);
        } else {
          Object.keys(data).map(key => showToast({ message: `${key} ${data[key]}`, type: 'error' }));
        }
      })
      .catch((error) => showToast({ message: error.message, type: 'error' }));
  };

  // Handles issue where values were being dropped due to stale data
  const editSeasonProps = {
    seasonOnEdit, setSeasonOnEdit, onUpdate, seasonUpdateErrors, setSeasonUpdateErrors,
  };

  const editingSeasonsData = seasonOnEdit.id === null
    ? seasonsData
    : seasonsData.map((season) => (season.id === seasonOnEdit.id
      ? EditSeason({ season, ...editSeasonProps })
      : season));

  const displayedSeasonsData = updatedSeasonId === null
    ? editingSeasonsData
    : editingSeasonsData.map((season) => (season.id === updatedSeasonId
      ? {
        ...season,
        action: (
          <div className="flex pl-4 space-x-3 ">
            <h4 className="text-vamos-black">Updated</h4>{' '}
            <FaRegCircleCheck size={24} color={tailwindTheme.theme.extend.colors['vamos-green1']} />
          </div>
        ),
      }
      : season));

  const tableColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      className: 'flex-1',
      isSortable: true,
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      className: 'flex-1',
      isSortable: true,
      render: (value) => (typeof value === 'string' ? formatNumericDate(value) : value),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      className: 'flex-1',
      isSortable: true,
      render: (value) => (typeof value === 'string' ? formatNumericDate(value) : value),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      className: 'min-w-[10rem]',
      isSortable: false,
    },
  ];

  useEffect(() => {
    setSeasonOnEdit(initialSeason);
    setUpdatedSeasonId(null);
    getSeasons(orgId, params)
      .then((response) => response.json())
      .then((data) => updateSeasonData(
        data,
        setSeasonsData,
        setTotalSeasons,
        isManager,
        isPlanner,
        showDeleteModal,
        handleExport,
        handleSeasonEdit,
        seasonsDataRef,
      ))
      .catch((error) => showToast({ message: error.message, type: 'error' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, params, showToast]);

  const onPageChange = (page) => {
    setSeasonOnEdit(initialSeason);
    setUpdatedSeasonId(null);
    setCurrentPage(page);

    const updatedParams = {
      ...params,
      offset: (page - 1) * itemsPerPage,
    };

    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: {
        ...updatedParams,
        page,
      },
    });
  };

  const onCreate = (data) => {
    // Check for an existing season with the same name
    const seasonExists = seasonsData.some((season) => season.name.toLowerCase() === data.name.toLowerCase() && season.name.toLowerCase() === data.name.toLowerCase());
    const errorsObject = { ...initialDataForValidationErrors };
    if (seasonExists) {
      errorsObject.name = 'Season name already exists.';
    }
    if (data.name === '') {
      errorsObject.name = 'Name can’t be blank.';
    }
    if (data.start_date === '') {
      errorsObject.start_date = 'Start date can’t be blank.';
    }
    if (data.end_date === '') {
      errorsObject.end_date = 'End date can’t be blank.';
    }
    if (new Date(data.start_date).getTime() > new Date(data.end_date).getTime()) {
      errorsObject.end_date = `End date must be after ${formatImportDate(data.start_date)}.`;
    }
    if (Object.values(errorsObject).some((value) => value !== '')) {
      setValidationErrors(errorsObject);
      return;
    }

    createSeason(data, orgId)
      .then((resp) => {
        if (resp.ok) {
          showToast({ message: 'Season added successfully', type: 'success' });
          closeModal('addSeason');
          getSeasons(orgId, params)
            .then((response) => response.json())
            .then((newdata) => updateSeasonData(
              newdata,
              setSeasonsData,
              setTotalSeasons,
              isManager,
              isPlanner,
              showDeleteModal,
              handleExport,
              handleSeasonEdit,
              seasonsDataRef,
            ))
            .catch((error) => showToast({ message: error.message, type: 'error' }));
        }
        if (resp.status !== 422) return;
        resp.json()
          .then((respData) => {
            respData.map((err) => showToast({ message: err, type: 'error' }));
          });
      })
      .catch((error) => showToast({ message: error.message, type: 'error' }));
  };

  const breadcrumbLinks = [
    { href: '/admin', name: 'Admin' },
    { href: '/seasons', name: 'Seasons' },
  ];

  // Helpers to derive modal display data
  const findSeasonNameById = (id) => {
    if (!id) return '';
    const s = seasonsDataRef.current.find((x) => x.id === Number(id));
    return s?.name || '';
  };

  const exportModalData = {
    show: !!modals.exportSeasonId?.isOpen,
    id: Number(modals.exportSeasonId?.value || 0),
    name: findSeasonNameById(modals.exportSeasonId?.value),
    completed: exportStatus.completed,
    failed: exportStatus.failed,
  };

  return (
    <div className="mx-2.75">
      <header>
        {/* Breadcrumbs */}
        <Breadcrumbs links={breadcrumbLinks} />
      </header>
      {seasonsData.length > 0 ? (
        <>
          <div className="flex w-full justify-between items-center pb-4">
            <PagesHeader name="season" label="Seasons" size="h-9" />
            <Button
              label="New Season"
              onClick={() => {
                setSeasonOnEdit(initialSeason);
                setUpdatedSeasonId(null);
                openModal('addSeason');
              }}
              Icon={Plus}
            />
          </div>
          <Table
            dataSource={displayedSeasonsData}
            columns={tableColumns}
            onSort={(values) => {
              setSeasonOnEdit(initialSeason);
              setUpdatedSeasonId(null);
              const updatedParams = {
                ...params,
                sortBy: values.field,
                direction: values.order,
                offset: 0,
              };

              setCurrentPage(1);
              setParams(updatedParams);

              updateSearchParamsAndUrl({
                params: {
                  ...updatedParams,
                  page: 1,
                },
              });
            }}
            typeOfData="seasons"
            DetailComponent={SummaryComponent}
          />
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={totalSeasons}
            currentPage={currentPage}
            setCurrentPage={onPageChange}
            marginTop="my-9"
          />
          {exportModalData.show && (
            <ExportSeasonsModal
              data={exportModalData}
              handleClose={() => {
                setExportStatus({ completed: false, failed: false });
                closeModal('exportSeasonId');
              }}
            />
          )}
        </>
      ) : (
        <EmptyState
          pageType="seasons"
          onAdd={() => openModal('addSeason')}
        />
      )}
      {modals.addSeason?.isOpen && (
        <NewSeasonModal
          isShowing={modals.addSeason.isOpen}
          handleClose={() => { closeModal('addSeason'); setValidationErrors(initialDataForValidationErrors); }}
          onCreate={onCreate}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
        />
      )}
    </div>
  );
};

SeasonsPage.propTypes = {
  organization: PropTypes.object.isRequired,
  currentUserRole: PropTypes.string.isRequired,
};

export default withContextProvider(DialogsProvider)(withContextProvider(ToastProvider)(SeasonsPage));

